import { useEffect, useMemo, useState, type FormEvent } from "react";
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import {
  apiFetch,
  enableDirectApiSession,
  microsoftLoginUrl,
  microsoftPasswordResetUrl,
  type AuthLookup,
  type ApiSessionInfo,
  type ApiUser,
  type PublicSettings,
} from "../lib/api";
import { saveToken } from "../lib/auth";
import { preloadDashboard } from "../lib/dashboard-loader";
import { confirmLoginSession, initialSessionCheckTimeoutMs } from "../lib/session-check";
import { currentDashboardReturnPath, safeAppReturnPath } from "../lib/authReturnPath";
import { platformModules, translations, type Locale } from "../lib/i18n";
import { resolveThemeMode, type ThemeMode } from "../lib/theme";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { TurnstileWidget } from "./TurnstileWidget";
import { isNativeApp, startNativeMicrosoftLogin } from "../lib/native-app";

function MicrosoftLogo() {
  return (
    <span className="ms-logo" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
  );
}

function AccentedLoginTitle({ title, accent }: { title: string; accent: string }) {
  const index = title.toLowerCase().lastIndexOf(accent.toLowerCase());
  if (index < 0) return <>{title}</>;

  return (
    <>
      {title.slice(0, index)}
      <span className="title-accent">{title.slice(index, index + accent.length)}</span>
      {title.slice(index + accent.length)}
    </>
  );
}

const defaultHeroImage = "/assets/login-hero.webp";
const defaultHeroSrcSet = "/assets/login-hero-768.webp 768w, /assets/login-hero.webp 1536w";

export function LoginScreen({
  locale,
  onLocaleChange,
  themeMode,
  onThemeChange,
  sessionExpiredNotice = false,
}: {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  themeMode: ThemeMode;
  onThemeChange: (mode: ThemeMode) => void;
  sessionExpiredNotice?: boolean;
}) {
  const t = translations[locale];
  const [sessionExpiredFromQuery, setSessionExpiredFromQuery] = useState(() => new URLSearchParams(window.location.search).get("session_expired") === "1");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [requestingReset, setRequestingReset] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const [turnstileResetNonce, setTurnstileResetNonce] = useState(0);
  const [lookup, setLookup] = useState<{
    status: "idle" | "loading" | "found" | "not_found" | "inactive" | "error";
    account: AuthLookup | null;
  }>({ status: "idle", account: null });
  const [settings, setSettings] = useState<PublicSettings>({
    schoolDomain: "bgt.school",
    loginHeroUrl: null,
    turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY || null,
    stagingLoginEnabled: false,
  });

  useEffect(() => {
    apiFetch<PublicSettings>("/api/public/settings")
      .then(setSettings)
      .catch(() => undefined);

    const params = new URLSearchParams(window.location.search);
    const initialError = params.get("error");
    const sessionExpired = params.get("session_expired") === "1";
    if (sessionExpired) {
      setSessionExpiredFromQuery(true);
      window.history.replaceState({}, "", "/");
    }
    if (initialError) {
      setError(initialError);
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const normalizedEmail = email.trim().toLowerCase();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  const accountFound = lookup.status === "found" && lookup.account?.exists && lookup.account.isActive;
  const schoolAccount = Boolean(emailValid && accountFound && lookup.account?.authProvider === "microsoft");
  const externalAccount = Boolean(emailValid && accountFound && lookup.account?.authProvider === "local");
  const checkingAccount = emailValid && lookup.status === "loading";
  const accountMessage = emailValid && lookup.status === "loading"
    ? t.checkingAccount
    : emailValid && lookup.status === "not_found"
      ? t.accountNotFound
      : emailValid && lookup.status === "inactive"
        ? t.accountInactive
        : null;
  const turnstileSiteKey = settings.turnstileSiteKey || import.meta.env.VITE_TURNSTILE_SITE_KEY || null;
  const stagingLoginEnabled = settings.stagingLoginEnabled === true;
  const canSubmit = schoolAccount || (externalAccount && password.length > 0 && (stagingLoginEnabled || Boolean(turnstileToken)));
  const heroImage = settings.loginHeroUrl || defaultHeroImage;
  const modules = platformModules(locale);
  const turnstileTheme = resolveThemeMode(themeMode);
  const loginTitleAccent = locale === "sq" ? "tuaj" : "account";

  useEffect(() => {
    setPassword("");
    setResetSent(false);
    setTurnstileToken(null);
    setTurnstileError(null);

    if (!emailValid) {
      setLookup({ status: "idle", account: null });
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setLookup({ status: "loading", account: null });
      apiFetch<AuthLookup>(`/api/auth/lookup?email=${encodeURIComponent(normalizedEmail)}`, {
        signal: controller.signal,
      })
        .then((account) => {
          if (!account.exists) {
            setLookup({ status: "not_found", account });
            return;
          }
          if (!account.isActive) {
            setLookup({ status: "inactive", account });
            return;
          }
          setLookup({ status: "found", account });
          preloadDashboard();
        })
        .catch((requestError) => {
          if (requestError instanceof DOMException && requestError.name === "AbortError") return;
          setLookup({ status: "error", account: null });
        });
    }, 280);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [emailValid, normalizedEmail]);

  const methodCopy = useMemo(() => {
    if (schoolAccount) return { title: t.schoolAccount, text: t.schoolAccountText };
    if (externalAccount) return { title: t.externalAccount, text: t.externalAccountText };
    return null;
  }, [externalAccount, schoolAccount, t]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    setError(null);
    if (!emailValid || !canSubmit) return;
    const targetModule = new URLSearchParams(window.location.search).get("module") === "studentCare" ? "studentCare" : undefined;
    const loginReturnPath = safeAppReturnPath(new URLSearchParams(window.location.search).get("return_to"), window.location.origin);
    const returnPath = loginReturnPath || currentDashboardReturnPath(window.location.pathname, window.location.search);

    if (schoolAccount && isNativeApp()) {
      startNativeMicrosoftLogin(normalizedEmail, returnPath || undefined).catch(() => setError("Faqja e Microsoft nuk u hap. Provo përsëri."));
      return;
    }
    if (schoolAccount) {
      window.location.assign(microsoftLoginUrl(normalizedEmail, { targetModule, returnPath: returnPath || undefined }));
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiFetch<{ session: ApiSessionInfo; user: ApiUser }>(
        stagingLoginEnabled ? "/api/auth/staging/login" : "/api/auth/local/login",
        {
        method: "POST",
        body: JSON.stringify({ email: normalizedEmail, password, turnstileToken }),
        },
      );
      const verified = await confirmLoginSession(result.user.id, () => apiFetch<{ session: ApiSessionInfo; user: ApiUser }>("/api/auth/me", {
        suppressUnauthorizedSessionEvent: true, signal: AbortSignal.timeout(initialSessionCheckTimeoutMs),
      }));
      saveToken(null, verified.session);
      await enableDirectApiSession(result.user.id);
      window.location.assign(returnPath || (targetModule === "studentCare" ? "/dashboard?module=studentCare" : "/dashboard"));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.callbackError);
      setTurnstileResetNonce((current) => current + 1);
      setSubmitting(false);
    }
  }

  async function requestPasswordReset() {
    if (!externalAccount || !turnstileToken) return;
    setRequestingReset(true);
    setError(null);
    try {
      await apiFetch<{ ok: true }>("/api/auth/password-reset/request", {
        method: "POST",
        body: JSON.stringify({ email: normalizedEmail, turnstileToken }),
      });
      setResetSent(true);
      setTurnstileResetNonce((current) => current + 1);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.callbackError);
      setTurnstileResetNonce((current) => current + 1);
    } finally {
      setRequestingReset(false);
    }
  }

  const showSessionExpiredNotice = sessionExpiredNotice || sessionExpiredFromQuery;

  return (
    <main className="login-shell">
      <section className="visual-panel" aria-label={t.productName}>
        <img
          className="visual-image"
          src={heroImage}
          srcSet={settings.loginHeroUrl ? undefined : defaultHeroSrcSet}
          sizes="(max-width: 820px) 100vw, 52vw"
          alt=""
          fetchPriority="high"
          onError={(event) => {
            if (event.currentTarget.src.endsWith(defaultHeroImage)) return;
            event.currentTarget.srcset = defaultHeroSrcSet;
            event.currentTarget.src = defaultHeroImage;
          }}
        />
        <div className="visual-scrim" />
        <a className="visual-site-link" href="https://www.bgt.school" target="_blank" rel="noreferrer">
          bgt.school <ArrowRight size={16} />
        </a>
        <div className="visual-module-pills" aria-label={t.modulesTitle}>
          {modules.map((module) => (
            <span key={module.title}>{module.title}</span>
          ))}
        </div>
      </section>

      <section className="form-panel">
        <div className="login-topbar">
          <a className="compact-brand" href="https://www.bgt.school" target="_blank" rel="noreferrer">
            <img className="brand-logo brand-logo-light" src="/assets/bgt-logo.png" alt="BGT" />
            <img className="brand-logo brand-logo-dark" src="/assets/bgt-logo-white.webp" alt="" aria-hidden="true" />
            <span>{t.productName}</span>
          </a>
          <div className="topbar-controls">
            <ThemeSwitcher locale={locale} mode={themeMode} onChange={onThemeChange} />
            <LanguageSwitcher locale={locale} onChange={onLocaleChange} compact />
          </div>
        </div>

        <div className="form-wrap">
          <div className="login-slogan" aria-label="Making IT Work">
            <span>MAKING</span>
            <strong>IT</strong>
            <span>WORK</span>
          </div>

          <div className="form-heading">
            <h1>
              <AccentedLoginTitle title={t.loginTitle} accent={loginTitleAccent} />
            </h1>
            <p>{t.loginSubtitle}</p>
          </div>

          <form className="login-form" onSubmit={submit} noValidate>
            {stagingLoginEnabled ? (
              <div className="session-expired-notice" role="status">
                <ShieldCheck size={18} />
                <span>{locale === "sq" ? "Mjedis testues BTEC — pa të dhëna reale." : "BTEC staging environment — no real data."}</span>
              </div>
            ) : null}
            {showSessionExpiredNotice ? (
              <div className="session-expired-notice" role="status">
                <ShieldCheck size={18} />
                <span>{t.loginSessionExpired}</span>
              </div>
            ) : null}
            <label className="field-label" htmlFor="email">
              {t.email}
            </label>
            <div className={`input-shell${touched && !emailValid && email ? " invalid" : ""}`}>
              <Mail size={20} aria-hidden="true" />
              <input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t.emailPlaceholder}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError(null);
                  setResetSent(false);
                }}
                onBlur={() => setTouched(true)}
                aria-invalid={touched && Boolean(email) && !emailValid}
              />
            </div>
            {touched && email && !emailValid ? <span className="field-error">{t.invalidEmail}</span> : null}
            {accountMessage ? (
              <div className={`account-message ${lookup.status === "loading" ? "checking" : "blocked"}`} role="status">
                {accountMessage}
              </div>
            ) : null}

            {methodCopy ? (
              <div className="login-method" key={schoolAccount ? "microsoft" : "local"}>
                <ShieldCheck size={19} />
                <div>
                  <strong>{methodCopy.title}</strong>
                  <span>{methodCopy.text}</span>
                </div>
              </div>
            ) : null}

            {externalAccount ? (
              <div className="password-stage">
                <label className="field-label" htmlFor="password">
                  {t.password}
                </label>
                <div className="input-shell">
                  <LockKeyhole size={20} aria-hidden="true" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder={t.passwordPlaceholder}
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setError(null);
                    }}
                  />
                  <button
                    className="password-toggle"
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? t.hidePassword : t.showPassword}
                  >
                    {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                  </button>
                </div>
              </div>
            ) : null}

            {externalAccount && !stagingLoginEnabled && turnstileSiteKey ? (
              <div className="turnstile-stage">
                <TurnstileWidget
                  siteKey={turnstileSiteKey}
                  theme={turnstileTheme}
                  locale={locale}
                  resetNonce={turnstileResetNonce}
                  onToken={(token) => {
                    setTurnstileToken(token);
                    if (token) setTurnstileError(null);
                  }}
                  onError={() => setTurnstileError(t.securityCheckFailed)}
                />
              </div>
            ) : null}
            {externalAccount && !stagingLoginEnabled && !turnstileSiteKey ? <div className="form-error">{t.securityUnavailable}</div> : null}
            {turnstileError ? <div className="form-error" role="alert">{turnstileError}</div> : null}

            {error ? <div className="form-error" role="alert">{error}</div> : null}

            <button className={`login-submit${schoolAccount ? " microsoft" : ""}`} type="submit" disabled={!canSubmit || submitting || checkingAccount}>
              {schoolAccount ? <MicrosoftLogo /> : null}
              <span>
                {submitting ? t.signingIn : checkingAccount ? t.checkingAccount : schoolAccount ? t.microsoft : externalAccount ? t.signIn : t.continue}
              </span>
              {!schoolAccount ? <ArrowRight size={19} /> : null}
            </button>

            {emailValid && accountFound && !stagingLoginEnabled ? (
              <div className="forgot-row">
                <span>{t.forgotPassword}</span>
                {schoolAccount ? (
                  <a href={microsoftPasswordResetUrl(normalizedEmail)} target="_blank" rel="noreferrer">
                    {t.microsoftResetHint}
                  </a>
                ) : (
                  <button type="button" disabled={requestingReset || resetSent} onClick={() => void requestPasswordReset()}>
                    {t.requestReset}
                  </button>
                )}
              </div>
            ) : null}
            {resetSent ? <div className="reset-confirmation"><ShieldCheck size={17} />{t.requestSent}</div> : null}
          </form>

          <div className="access-note">
            <ShieldCheck size={17} />
            <p>
              {t.accessOnly} <a href="https://www.bgt.school/contact" target="_blank" rel="noreferrer">{t.contactLink}</a>
            </p>
          </div>
        </div>

        <p className="login-footer">© {new Date().getFullYear()} {t.productName}</p>
      </section>
    </main>
  );
}
